import { PortableText } from "@portabletext/react"
import type { PortableTextComponents } from "@portabletext/react"

import type { PortableTextBlock } from "@portabletext/types"

import { normalize_marketing_external_href } from "./link_urls"

interface PortableTextBlockProps {
  value: PortableTextBlock[] | null | undefined
  className?: string
}

const components: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p class="my-5 leading-relaxed text-indigo-900 first:mt-0">{children}</p>,
    h1: ({ children }) => (
      <h1 class="font-display mt-12 text-3xl font-bold tracking-tight text-indigo-950 first:mt-0 md:text-4xl">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 class="font-display mt-10 text-2xl font-bold tracking-tight text-indigo-950 first:mt-0 md:text-3xl">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 class="font-display mt-8 text-xl font-bold tracking-tight text-indigo-950 md:text-2xl">{children}</h3>
    ),
    blockquote: ({ children }) => (
      <blockquote class="my-8 border-l-4 border-indigo bg-indigo-50 py-1 pl-5 pr-2 text-lg italic text-indigo-900">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul class="my-5 list-disc space-y-2 pl-6 text-indigo-900 marker:text-indigo">{children}</ul>
    ),
    number: ({ children }) => (
      <ol class="my-5 list-decimal space-y-2 pl-6 text-indigo-900 marker:font-semibold marker:text-indigo">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li class="leading-relaxed">{children}</li>,
    number: ({ children }) => <li class="leading-relaxed">{children}</li>,
  },
  marks: {
    link: ({ children, value }) => {
      const raw = typeof value?.href === "string" ? value.href : "#"
      const href = normalize_marketing_external_href(raw)
      const blank = value?.blank !== false
      return (
        <a
          href={href}
          class="font-medium text-indigo underline decoration-indigo-300 underline-offset-2 transition-colors hover:text-indigo-800"
          target={blank ? "_blank" : undefined}
          rel={blank ? "noopener noreferrer" : undefined}>
          {children}
        </a>
      )
    },
    strong: ({ children }) => <strong class="font-bold text-indigo-950">{children}</strong>,
    em: ({ children }) => <em class="italic text-indigo-900">{children}</em>,
    code: ({ children }) => (
      <code class="rounded-md bg-sky-100 px-1.5 py-0.5 font-mono text-[0.9em] font-medium text-indigo-950">
        {children}
      </code>
    ),
  },
}

export function PortableTextBlock(props: PortableTextBlockProps) {
  const { value, className } = props
  if (!value?.length) {
    return null
  }
  return (
    <div class={className ?? "prose-blog max-w-none font-content text-indigo-900"}>
      <PortableText value={value} components={components} />
    </div>
  )
}
